import React from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import { Bell, Cake, ChevronRight } from 'lucide-react';
import { adviserFactsheetOf } from '../../../../utils/client-factsheet';
import { avatarGenderClass, clientNameOf } from '../clientPresentation';
import type { CalendarEntry } from '../reminderCalendar';
import type { IntlLike } from '../clientPresentation';

/**
 * The selected day's entries. Presentational by contract (spec D11): it reports
 * a CLIENT ID and nothing else, so the page above decides what opening a client
 * means — a drawer in V2, something else in V1.
 *
 * A birthday row opens the client like any other. Tapping a person's name
 * should reach that person; it should not fire a greeting on their behalf. The
 * greeting entry point stays where the row already keeps it, inside the drawer.
 */
export type ReminderDayListProps = {
  /** 'YYYY-MM-DD', or null when nothing is selected — then nothing renders. */
  day: string | null;
  entries: CalendarEntry[];
  adviserEmail?: string;
  onSelectClient: (clientId: string) => void;
  onClear: () => void;
  intl: IntlLike;
};

const initialOf = (client: CalendarEntry['client']): string => clientNameOf(client).trim().charAt(0) || '?';

const ReminderDayList = (props: ReminderDayListProps) => {
  const { day, entries, adviserEmail, onSelectClient, onClear, intl } = props;
  const message = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);

  if (!day) return null;

  return (
    <section className="clientv2-cal-day-list">
      <header className="clientv2-cal-day-head">
        <strong>{moment(day, 'YYYY-MM-DD').format('LL')}</strong>
        <button type="button" onClick={onClear} data-testid="cal-day-clear">
          {message('clientv2-cal-clear')}
        </button>
      </header>

      {entries.length === 0 && (
        <p className="clientv2-cal-day-empty" role="status">
          {message('clientv2-cal-day-empty')}
        </p>
      )}

      {entries.map(entry => {
        const isReminder = entry.kind === 'reminder';
        const note = adviserFactsheetOf(entry.client, adviserEmail)?.remark;
        return (
          <button
            key={`${entry.kind}-${entry.client._id}`}
            type="button"
            data-testid="cal-day-row"
            className="clientv2-cal-day-row"
            onClick={() => onSelectClient(entry.client._id)}
          >
            <span className="clientv2-cal-day-avatar-wrap">
              <span
                className={`clientv2-cal-day-avatar clientv2-av-${avatarGenderClass(entry.client, adviserEmail)}`}
                aria-hidden="true"
              >
                {initialOf(entry.client)}
              </span>
              <span
                className={`clientv2-cal-day-glyph${isReminder ? ' is-reminder' : ' is-birthday'}${
                  isReminder && entry.mark === 'due' ? ' is-due' : ''
                }`}
                aria-hidden="true"
              >
                {isReminder ? <Bell size={12} /> : <Cake size={12} />}
              </span>
            </span>

            <span className="clientv2-cal-day-body">
              <span className="clientv2-cal-day-name">
                {clientNameOf(entry.client)}
                <span className={`clientv2-cal-day-kind${isReminder ? ' is-reminder' : ' is-birthday'}`}>
                  {message(isReminder ? 'clientv2-cal-kind-reminder' : 'clientv2-cal-kind-birthday')}
                </span>
              </span>
              <span className="clientv2-cal-day-note">
                {isReminder ? note || '' : message('clientv2-cal-age', { age: entry.age })}
              </span>
            </span>

            <ChevronRight size={16} aria-hidden="true" className="clientv2-cal-day-chev" />
          </button>
        );
      })}
    </section>
  );
};

export default injectIntl(ReminderDayList);
